import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { ArrowRight, Check } from "lucide-react";
import { Button, Input } from "@bastion/ui";
import type { ToolRendererProps } from "@bastion/assistant";
import styles from "./ChoiceCard.module.css";

/**
 * Render args for the `ask_user_choice` tool. The model emits this when it
 * wants the user to disambiguate (Cursor-style A/B/C buttons + optional
 * free-text "Other" row). Picking an option sends a new user message with
 * the picked label (or `text:` prefix for free input) so the conversation
 * continues naturally.
 */
interface ChoiceOptionArg {
  id?: unknown;
  label?: unknown;
  description?: unknown;
}

interface ChoiceCardOption {
  id: string;
  label: string;
  description: string | null;
}

interface ChoiceCardArgs {
  question: string;
  options: ChoiceCardOption[];
  allowTextInput: boolean;
  textInputPlaceholder: string;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseChoiceCardArgs(raw: Record<string, unknown>): ChoiceCardArgs | null {
  const question = asString(raw.question);
  const optionsRaw = Array.isArray(raw.options) ? raw.options : [];
  const options: ChoiceCardOption[] = [];
  const seen = new Set<string>();
  for (const entry of optionsRaw) {
    if (!entry || typeof entry !== "object") continue;
    const opt = entry as ChoiceOptionArg;
    const id = asString(opt.id);
    const label = asString(opt.label);
    const description = asString(opt.description);
    if (!id || !label || seen.has(id)) continue;
    seen.add(id);
    options.push({
      id,
      label,
      description: description || null,
    });
  }
  if (!question || options.length < 2) {
    return null;
  }
  return {
    question,
    options,
    allowTextInput: Boolean(raw.allow_text_input),
    textInputPlaceholder: asString(raw.text_input_placeholder) || "Other…",
  };
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function letterFor(index: number, allowTextInput: boolean): string {
  return LETTERS[index] ?? "•";
}

function otherLetter(optionCount: number): string {
  return LETTERS[optionCount] ?? "•";
}

/** Compact result label the model receives in chat history. */
function formatChoiceAnswer(option: ChoiceCardOption): string {
  return option.label;
}

function formatTextAnswer(text: string): string {
  return text.trim();
}

export function ChoiceCard({ toolCall, onAnswer }: ToolRendererProps) {
  const parsed = useMemo(() => parseChoiceCardArgs(toolCall.args), [toolCall.args]);
  const [picked, setPicked] = useState<{ id: string; text: string } | null>(null);
  const [otherText, setOtherText] = useState("");
  const [otherActive, setOtherActive] = useState(false);
  const otherInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (otherActive) {
      otherInputRef.current?.focus();
    }
  }, [otherActive]);

  // When the model resends args for the same tool_call (rare), keep the user's
  // selection sticky so the UI doesn't flicker back to the unanswered state.
  const answered = toolCall.status === "executed" || toolCall.status === "cancelled";

  const handlePick = useCallback(
    (option: ChoiceCardOption) => {
      if (!onAnswer || picked || answered) {
        return;
      }
      const text = formatChoiceAnswer(option);
      setPicked({ id: option.id, text });
      onAnswer(toolCall.id, text);
    },
    [answered, onAnswer, picked, toolCall.id],
  );

  const handleOtherSubmit = useCallback(() => {
    if (!onAnswer || picked || answered) {
      return;
    }
    const text = formatTextAnswer(otherText);
    if (!text) {
      return;
    }
    setPicked({ id: "__other__", text });
    onAnswer(toolCall.id, text);
  }, [answered, onAnswer, otherText, picked, toolCall.id]);

  // Keyboard letter shortcuts: A/B/C… within the card's focus or while not
  // typing in the Other input. Designed to feel like Cursor's choice prompts.
  useEffect(() => {
    if (!parsed || picked || answered) {
      return undefined;
    }
    const handler = (event: KeyboardEvent) => {
      if (event.altKey || event.metaKey || event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        return;
      }
      const key = event.key.toUpperCase();
      const idx = LETTERS.indexOf(key);
      if (idx < 0) return;
      if (idx < parsed.options.length) {
        event.preventDefault();
        handlePick(parsed.options[idx]);
        return;
      }
      if (parsed.allowTextInput && idx === parsed.options.length) {
        event.preventDefault();
        setOtherActive(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [answered, handlePick, parsed, picked]);

  if (!parsed) {
    return null;
  }

  if (picked) {
    return (
      <div className={styles.card} data-testid="choice-card" data-state="answered">
        <p className={styles.question}>{parsed.question}</p>
        <div className={styles.answered}>
          <Check className={styles.answeredIcon} aria-hidden />
          <span className={styles.answeredText}>{picked.text}</span>
        </div>
      </div>
    );
  }

  const showOther = parsed.allowTextInput;

  return (
    <div
      ref={containerRef}
      className={styles.card}
      data-testid="choice-card"
      data-state="awaiting"
    >
      <p className={styles.question}>{parsed.question}</p>
      <ul className={styles.options} role="list">
        {parsed.options.map((option, idx) => (
          <li key={option.id}>
            <button
              type="button"
              className={styles.option}
              onClick={() => handlePick(option)}
              data-testid={`choice-card-option-${option.id}`}
            >
              <span className={styles.letter} aria-hidden>
                {letterFor(idx, showOther)}
              </span>
              <span className={styles.optionBody}>
                <span className={styles.optionLabel}>{option.label}</span>
                {option.description ? (
                  <span className={styles.optionDescription}>{option.description}</span>
                ) : null}
              </span>
              <ArrowRight
                className={styles.optionChevron}
                style={{ width: 14, height: 14 }}
                aria-hidden
              />
            </button>
          </li>
        ))}
        {showOther ? (
          <li>
            {otherActive ? (
              <div className={styles.otherActive}>
                <span className={styles.letter} aria-hidden>
                  {otherLetter(parsed.options.length)}
                </span>
                <Input
                  ref={otherInputRef}
                  value={otherText}
                  onChange={(event) => setOtherText(event.target.value)}
                  placeholder={parsed.textInputPlaceholder}
                  className={styles.otherInput}
                  onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleOtherSubmit();
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      setOtherActive(false);
                      setOtherText("");
                    }
                  }}
                  data-testid="choice-card-other-input"
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleOtherSubmit}
                  disabled={!otherText.trim()}
                  data-testid="choice-card-other-submit"
                >
                  Send
                </Button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.option}
                onClick={() => setOtherActive(true)}
                data-testid="choice-card-other"
              >
                <span className={styles.letter} aria-hidden>
                  {otherLetter(parsed.options.length)}
                </span>
                <span className={styles.optionBody}>
                  <span className={styles.optionLabel}>Other…</span>
                  <span className={styles.optionDescription}>
                    Type a custom reply
                  </span>
                </span>
                <ArrowRight
                  className={styles.optionChevron}
                  style={{ width: 14, height: 14 }}
                  aria-hidden
                />
              </button>
            )}
          </li>
        ) : null}
      </ul>
    </div>
  );
}
